import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowLeft } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { PackageQuiz } from '../components/PackageQuiz';
import { BounceHouseSelectionModal } from '../components/BounceHouseSelectionModal';
export const CartPage = () => {
  const {
    items,
    removeFromCart,
    clearCart,
    totalPrice,
    addToCart,
    canAddItem
  } = useCart();
  const navigate = useNavigate();
  const [showBounceHouseModal, setShowBounceHouseModal] = useState(false);
  const [pendingPackage, setPendingPackage] = useState<any>(null);
  const handleCheckout = () => {
    navigate('/booking-request');
  };
  // All available packages
  const allPackages = [{
    name: 'Tiny Tot',
    type: 'softplay' as const,
    packageValue: 'tiny-tot',
    price: '$325',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4WktozD23sUFeswQq2cCuq/77BCF6CB-EA89-44A4-BDDF-18A46698D723-1024x768.jpg',
    description: 'Perfect for 5 or fewer kids in smaller spaces'
  }, {
    name: 'Mega Tot',
    type: 'softplay' as const,
    packageValue: 'mega-tot',
    price: '$375',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bj3F22sb2nuKqenpirTHh1/IMG_4577-1024x768.jpg',
    description: 'Great for 6-10 kids with combo bounce house'
  }, {
    name: 'Gigantatot',
    type: 'softplay' as const,
    packageValue: 'gigantatot',
    price: '$450',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/71zAVFXd9bzmKmHYJ4QCA8/image.png',
    description: 'Perfect for 11-20 kids with two bounce houses'
  }, {
    name: 'Fancy Tot',
    type: 'softplay' as const,
    packageValue: 'fancy-tot',
    price: '$400',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uU6ZBP89bA24T6gpbVzeEX/image.png',
    description: 'Luxury white aesthetic for 10-15 kids'
  }, {
    name: 'Mega Fancy Tot',
    type: 'softplay' as const,
    packageValue: 'mega-fancy-tot',
    price: '$475',
    image: 'https://t3.ftcdn.net/jpg/02/77/69/26/360_F_277692690_xAGq27kZEjCmDq8D3lcttSjM6IeUVgKI.jpg',
    description: 'Premium luxury setup for 12-20 kids'
  }, {
    name: 'Small Bounce House',
    type: 'bouncehouse' as const,
    packageValue: 'small-bounce-house',
    price: '$90',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/kHTQ5jrXVC67yJtPhD7f1i/image.png',
    description: 'Compact colorful bounce house'
  }, {
    name: 'Medium Bounce House',
    type: 'bouncehouse' as const,
    packageValue: 'medium-bounce-house',
    price: '$100',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1tAZyG4Y6ojbRvMccKvv3s/81df0i20SIL._AC_SL1500_.jpg',
    description: 'Medium size with slide'
  }, {
    name: 'Colorful Combo Bounce House',
    type: 'bouncehouse' as const,
    packageValue: 'combo-bounce-house',
    price: '$150',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gHRpHpQ744Tqwj57J7go3N/image.png',
    description: 'Large with ball pit and slide'
  }, {
    name: 'Medium White Bounce House',
    type: 'bouncehouse' as const,
    packageValue: 'medium-white-bounce-house',
    price: '$200',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vnJ8Vsi2yVGyEnBLbZNqzj/image.png',
    description: 'Elegant white design'
  }, {
    name: 'White Combo Bounce House',
    type: 'bouncehouse' as const,
    packageValue: 'mega-white-combo-bounce-house',
    price: '$225',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/42dN8ZAPRjx3pURCvvrteu/image.png',
    description: 'Premium white castle with towers'
  }, {
    name: 'White Combo Bounce House w/ Ball Pit',
    type: 'bouncehouse' as const,
    packageValue: 'combo-white-bounce-house',
    price: '$250',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rDecq4r6w9CqRJodkYNa3L/0C7B1578-7567-45F8-8CC7-AC72C34BAC1E-768x1024.jpg',
    description: 'Premium white with ball pit'
  }];
  // Filter packages based on what can be added to cart
  const availablePackages = allPackages.filter(pkg => {
    const validation = canAddItem({
      type: pkg.type,
      packageName: pkg.name,
      packageValue: pkg.packageValue,
      price: pkg.price
    });
    return validation.canAdd;
  });
  const handleAddToCart = (pkg: (typeof allPackages)[0]) => {
    // Special handling for Mega Fancy Tot - need to select bounce house first
    if (pkg.packageValue === 'mega-fancy-tot') {
      setPendingPackage(pkg);
      setShowBounceHouseModal(true);
      return;
    }
    const result = addToCart({
      type: pkg.type,
      packageName: pkg.name,
      packageValue: pkg.packageValue,
      price: pkg.price
    });
    if (!result.success) {
      alert(result.message || 'Unable to add item to cart');
    }
  };
  const handleBounceHouseSelection = (bounceHouse: string) => {
    if (!pendingPackage) return;
    const result = addToCart({
      type: pendingPackage.type,
      packageName: pendingPackage.name,
      packageValue: pendingPackage.packageValue,
      price: pendingPackage.price,
      selectedBounceHouse: bounceHouse
    });
    if (!result.success) {
      alert(result.message || 'Unable to add item to cart');
    }
    setPendingPackage(null);
  };
  const isEmpty = items.length === 0;
  return <>
      <BounceHouseSelectionModal isOpen={showBounceHouseModal} onClose={() => {
      setShowBounceHouseModal(false);
      setPendingPackage(null);
    }} onSelect={handleBounceHouseSelection} packageName="Mega Fancy Tot" data-id="element-688" />

      <div className="bg-white w-full min-h-screen" data-id="element-689">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-690">
          {/* Quiz Section - Only show when cart is empty */}
          {isEmpty && <div className="mb-12" data-id="element-691">
              <PackageQuiz data-id="element-692" />
            </div>}

          {/* Continue Shopping Link at Top */}
          <div className="mb-6" data-id="element-693">
            <Link to="/softplay" className="inline-flex items-center text-primary-500 hover:text-primary-600 font-medium transition" onClick={() => window.scrollTo(0, 0)} data-id="element-694">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-695" />
              Continue Shopping
            </Link>
          </div>

          <div className="mb-8" data-id="element-696">
            <h1 className="text-4xl font-bold text-primary-500 mb-2" data-id="element-697">
              Your Cart
            </h1>
            <p className="text-lg text-gray-600" data-id="element-698">
              {isEmpty ? 'Add packages to get started with your booking request' : 'Review your selections and submit your booking request'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-699">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-id="element-700">
              {isEmpty ? <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center" data-id="element-701">
                  <ShoppingBag className="mx-auto h-16 w-16 text-gray-400 mb-4" data-id="element-702" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-703">
                    No items in cart
                  </h3>
                  <p className="text-gray-600" data-id="element-704">
                    Browse our packages below to get started!
                  </p>
                </div> : items.map(item => <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-id="element-705">
                    <div className="flex justify-between items-start" data-id="element-706">
                      <div className="flex-grow" data-id="element-707">
                        <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-708">
                          {item.packageName}
                        </h3>
                        <p className="text-sm text-gray-500 mb-2" data-id="element-709">
                          {item.type === 'softplay' ? 'Soft Play Rental' : item.type === 'bouncehouse' ? 'Bounce House Rental' : 'Photobooth Rental'}
                        </p>
                        <p className="text-2xl font-bold text-green-800" data-id="element-710">
                          {item.price}
                        </p>

                        {/* Show selected bounce house for Mega Fancy Tot */}
                        {item.packageValue === 'mega-fancy-tot' && item.selectedBounceHouse && <p className="text-sm text-gray-600 mt-2" data-id="element-711">
                              Includes:{' '}
                              {item.selectedBounceHouse === 'combo-white-bounce-house' ? 'Combo White Bounce House w/ Ball Pit' : 'Combo White Bounce House'}
                            </p>}
                      </div>

                      <button onClick={() => removeFromCart(item.id)} className="ml-4 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors" aria-label="Remove item" data-id="element-712">
                        <Trash2 className="h-5 w-5" data-id="element-713" />
                      </button>
                    </div>
                  </div>)}

              {/* Available Packages Table */}
              {availablePackages.length > 0 && <div className="mt-8" data-id="element-714">
                  <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-715">
                    {isEmpty ? 'All Available Packages' : 'Add More to Your Cart'}
                  </h3>
                  <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden" data-id="element-716">
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto" data-id="element-717">
                      <table className="w-full" data-id="element-718">
                        <thead className="bg-gray-50 border-b border-gray-200" data-id="element-719">
                          <tr data-id="element-720">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900" data-id="element-721">
                              Package
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900" data-id="element-722">
                              Description
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900" data-id="element-723">
                              Price
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900" data-id="element-724">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200" data-id="element-725">
                          {availablePackages.map((pkg, index) => <tr key={index} className="hover:bg-gray-50 transition-colors" data-id="element-726">
                              <td className="px-4 py-3" data-id="element-727">
                                <div className="flex items-center gap-3" data-id="element-728">
                                  <img src={pkg.image} alt={pkg.name} className="w-12 h-12 object-cover rounded-lg shadow-sm" data-id="element-729" />
                                  <div data-id="element-730">
                                    <div className="font-semibold text-sm text-gray-900" data-id="element-731">
                                      {pkg.name}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-732">
                                      {pkg.type === 'softplay' ? 'Soft Play' : 'Bounce House'}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-600" data-id="element-733">
                                {pkg.description}
                              </td>
                              <td className="px-4 py-3" data-id="element-734">
                                <span className="text-sm font-bold text-green-800" data-id="element-735">
                                  {pkg.price}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right" data-id="element-736">
                                <button onClick={() => handleAddToCart(pkg)} className="inline-flex items-center px-3 py-1.5 bg-secondary-500 text-white rounded-md font-medium hover:bg-secondary-600 transition-all shadow-sm text-xs" data-id="element-737">
                                  Add
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden divide-y divide-gray-200" data-id="element-738">
                      {availablePackages.map((pkg, index) => <div key={index} className="p-3" data-id="element-739">
                          <div className="flex gap-3 mb-2" data-id="element-740">
                            <img src={pkg.image} alt={pkg.name} className="w-16 h-16 object-cover rounded-lg shadow-sm flex-shrink-0" data-id="element-741" />
                            <div className="flex-grow" data-id="element-742">
                              <h4 className="font-semibold text-sm text-gray-900 mb-1" data-id="element-743">
                                {pkg.name}
                              </h4>
                              <p className="text-xs text-gray-500 mb-1" data-id="element-744">
                                {pkg.type === 'softplay' ? 'Soft Play' : 'Bounce House'}
                              </p>
                              <p className="text-xs text-gray-600 mb-1" data-id="element-745">
                                {pkg.description}
                              </p>
                              <span className="text-sm font-bold text-green-800" data-id="element-746">
                                {pkg.price}
                              </span>
                            </div>
                          </div>
                          <button onClick={() => handleAddToCart(pkg)} className="w-full px-3 py-1.5 bg-secondary-500 text-white rounded-md font-medium hover:bg-secondary-600 transition-all shadow-sm text-sm" data-id="element-747">
                            Add to Cart
                          </button>
                        </div>)}
                    </div>
                  </div>
                </div>}
            </div>

            {/* Cart Summary */}
            <div className="lg:col-span-1" data-id="element-748">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 sticky top-4" data-id="element-749">
                <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-750">
                  Cart Summary
                </h2>

                <div className="space-y-2 mb-4" data-id="element-751">
                  <div className="flex justify-between text-gray-600" data-id="element-752">
                    <span data-id="element-753">Items ({items.length})</span>
                    <span data-id="element-754">${totalPrice}</span>
                  </div>
                </div>

                <div className="border-t border-gray-300 pt-4 mb-6" data-id="element-755">
                  <div className="flex justify-between text-xl font-bold text-gray-900" data-id="element-756">
                    <span data-id="element-757">Estimated Total</span>
                    <span data-id="element-758">${totalPrice}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2" data-id="element-759">
                    {isEmpty ? 'Add packages to see your estimated total' : 'Final pricing may vary based on delivery location and event details'}
                  </p>
                </div>

                <button onClick={handleCheckout} disabled={isEmpty} className={`w-full py-3 px-6 rounded-md font-medium transition-all shadow-md mb-3 ${isEmpty ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-secondary-500 text-white hover:bg-secondary-600'}`} data-id="element-760">
                  Request Booking
                </button>

                {!isEmpty && <button onClick={clearCart} className="w-full bg-white text-gray-700 py-2 px-6 rounded-md font-medium border border-gray-300 hover:bg-gray-50 transition-all mb-4" data-id="element-761">
                    Clear Cart
                  </button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
};